import { useRef, useState, useCallback, useEffect } from 'react';
import * as faceapi from '@vladmandic/face-api';
import { Eye, RotateCcw, Smile, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';

type ChallengeType = 'BLINK' | 'TURN_LEFT' | 'TURN_RIGHT' | 'SMILE';

interface ChallengeResult {
  type: ChallengeType;
  completed: boolean;
  confidence: number;
  timestamp: Date;
}

interface LivenessChallengeProps {
  challenges?: ChallengeType[];
  timeout?: number;
  onComplete: (results: ChallengeResult[], score: number) => void;
  onError?: (error: string) => void;
  className?: string;
}

const CHALLENGE_ICONS: Record<ChallengeType, React.ReactNode> = {
  BLINK: <Eye className="w-8 h-8" />,
  TURN_LEFT: <RotateCcw className="w-8 h-8" />,
  TURN_RIGHT: <RotateCcw className="w-8 h-8 transform scale-x-[-1]" />,
  SMILE: <Smile className="w-8 h-8" />,
};

const CHALLENGE_INSTRUCTIONS: Record<ChallengeType, string> = {
  BLINK: 'Pisque os olhos 2 vezes',
  TURN_LEFT: 'Vire a cabeça para a esquerda',
  TURN_RIGHT: 'Vire a cabeça para a direita',
  SMILE: 'Sorria naturalmente',
};

const DEFAULT_CHALLENGES: ChallengeType[] = ['BLINK', 'TURN_LEFT', 'SMILE'];
const DEFAULT_TIMEOUT = 60;

export function LivenessChallenge({
  challenges = DEFAULT_CHALLENGES,
  timeout = DEFAULT_TIMEOUT,
  onComplete,
  onError,
  className = '',
}: LivenessChallengeProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationRef = useRef<number | null>(null);

  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [currentChallengeIndex, setCurrentChallengeIndex] = useState(0);
  const [results, setResults] = useState<ChallengeResult[]>([]);
  const [timeRemaining, setTimeRemaining] = useState(timeout);
  const [error, setError] = useState<string | null>(null);
  const [blinkCount, setBlinkCount] = useState(0);
  const [lastEyeState, setLastEyeState] = useState<'open' | 'closed'>('open');

  const currentChallenge = challenges[currentChallengeIndex];

  // Load face-api models
  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL = '/models';
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
          faceapi.nets.faceExpressionNet.loadFromUri(MODEL_URL),
        ]);
        setModelsLoaded(true);
      } catch (err) {
        const errorMsg = 'Erro ao carregar modelos de detecção facial';
        setError(errorMsg);
        onError?.(errorMsg);
      }
    };
    loadModels();
  }, [onError]);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      const errorMsg = 'Erro ao acessar câmera';
      setError(errorMsg);
      onError?.(errorMsg);
    }
  }, [onError]);

  const stopCamera = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const calculateEAR = (landmarks: faceapi.FaceLandmarks68) => {
    // Eye Aspect Ratio for blink detection
    const leftEye = landmarks.getLeftEye();
    const rightEye = landmarks.getRightEye();

    const eyeAR = (eye: faceapi.Point[]) => {
      const vertical1 = Math.sqrt(Math.pow(eye[1].x - eye[5].x, 2) + Math.pow(eye[1].y - eye[5].y, 2));
      const vertical2 = Math.sqrt(Math.pow(eye[2].x - eye[4].x, 2) + Math.pow(eye[2].y - eye[4].y, 2));
      const horizontal = Math.sqrt(Math.pow(eye[0].x - eye[3].x, 2) + Math.pow(eye[0].y - eye[3].y, 2));
      return (vertical1 + vertical2) / (2.0 * horizontal);
    };

    return (eyeAR(leftEye) + eyeAR(rightEye)) / 2;
  };

  const detectFace = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || !isRunning) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const displaySize = { width: video.videoWidth, height: video.videoHeight };
    
    faceapi.matchDimensions(canvas, displaySize);

    const detection = await faceapi
      .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
      .withFaceLandmarks()
      .withFaceExpressions();

    if (detection) {
      const resizedDetection = faceapi.resizeResults(detection, displaySize);
      const ctx = canvas.getContext('2d');
      
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        faceapi.draw.drawDetections(canvas, [resizedDetection]);
        faceapi.draw.drawFaceLandmarks(canvas, [resizedDetection]);
      }

      // Check current challenge
      const landmarks = detection.landmarks;
      const expressions = detection.expressions;
      const jaw = landmarks.getJawOutline();
      
      // Calculate head rotation (simplified)
      const noseX = landmarks.getNose()[0].x;
      const leftJaw = jaw[0].x;
      const rightJaw = jaw[16].x;
      const faceWidth = rightJaw - leftJaw;
      const nosePosition = (noseX - leftJaw) / faceWidth;

      let challengeCompleted = false;
      let confidence = 0;

      switch (currentChallenge) {
        case 'BLINK': {
          const ear = calculateEAR(landmarks);
          const isEyeClosed = ear < 0.2;
          
          if (lastEyeState === 'open' && isEyeClosed) {
            setLastEyeState('closed');
          } else if (lastEyeState === 'closed' && !isEyeClosed) {
            setLastEyeState('open');
            const newBlinkCount = blinkCount + 1;
            setBlinkCount(newBlinkCount);
            if (newBlinkCount >= 2) {
              challengeCompleted = true;
              confidence = 0.95;
            }
          }
          break;
        }
        case 'TURN_LEFT': {
          if (nosePosition < 0.35) {
            challengeCompleted = true;
            confidence = Math.min(1, (0.5 - nosePosition) * 3);
          }
          break;
        }
        case 'TURN_RIGHT': {
          if (nosePosition > 0.65) {
            challengeCompleted = true;
            confidence = Math.min(1, (nosePosition - 0.5) * 3);
          }
          break;
        }
        case 'SMILE': {
          const smileScore = expressions.happy;
          if (smileScore > 0.7) {
            challengeCompleted = true;
            confidence = smileScore;
          }
          break;
        }
      }

      if (challengeCompleted) {
        const result: ChallengeResult = {
          type: currentChallenge,
          completed: true,
          confidence,
          timestamp: new Date(),
        };

        const newResults = [...results, result];
        setResults(newResults);
        setBlinkCount(0);
        setLastEyeState('open');

        if (currentChallengeIndex < challenges.length - 1) {
          setCurrentChallengeIndex(prev => prev + 1);
        } else {
          // All challenges completed
          setIsRunning(false);
          const score = newResults.reduce((acc, r) => acc + r.confidence, 0) / newResults.length;
          onComplete(newResults, score);
          stopCamera();
          return;
        }
      }
    }

    animationRef.current = requestAnimationFrame(detectFace);
  }, [isRunning, currentChallenge, currentChallengeIndex, challenges, results, blinkCount, lastEyeState, onComplete, stopCamera]);

  // Timer countdown
  useEffect(() => {
    if (!isRunning) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          setIsRunning(false);
          stopCamera();
          
          // Mark remaining challenges as failed
          const failedResults: ChallengeResult[] = challenges
            .slice(currentChallengeIndex)
            .map(type => ({
              type,
              completed: false,
              confidence: 0,
              timestamp: new Date(),
            }));
          
          const allResults = [...results, ...failedResults];
          const score = allResults.filter(r => r.completed).length / allResults.length;
          onComplete(allResults, score);
          
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isRunning, challenges, currentChallengeIndex, results, onComplete, stopCamera]);

  // Start face detection loop
  useEffect(() => {
    if (isRunning && modelsLoaded) {
      detectFace();
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isRunning, modelsLoaded, detectFace]);

  const startChallenge = async () => {
    setResults([]);
    setCurrentChallengeIndex(0);
    setTimeRemaining(timeout);
    setBlinkCount(0);
    setLastEyeState('open');
    await startCamera();
    setIsRunning(true);
  };

  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 bg-red-50 rounded-lg ${className}`}>
        <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
        <p className="text-red-600 text-center">{error}</p>
      </div>
    );
  }

  if (!modelsLoaded) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 ${className}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" />
        <p className="text-gray-600">Carregando modelos de detecção...</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center ${className}`}>
      {/* Progress indicators */}
      <div className="flex gap-2 mb-4">
        {challenges.map((challenge, index) => {
          const result = results.find(r => r.type === challenge);
          const isActive = index === currentChallengeIndex && isRunning;
          
          return (
            <div
              key={challenge}
              className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
                result?.completed
                  ? 'bg-green-500 text-white'
                  : isActive
                  ? 'bg-blue-500 text-white animate-pulse'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {result?.completed ? (
                <CheckCircle className="w-5 h-5" />
              ) : result && !result.completed ? (
                <XCircle className="w-5 h-5" />
              ) : (
                <span className="text-sm font-medium">{index + 1}</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Video container */}
      <div className="relative w-full max-w-md">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full rounded-lg shadow-lg bg-black"
          style={{ transform: 'scaleX(-1)' }}
        />
        <canvas
          ref={canvasRef}
          className="absolute top-0 left-0 w-full h-full"
          style={{ transform: 'scaleX(-1)' }}
        />

        {/* Timer */}
        {isRunning && (
          <div className="absolute top-4 right-4 flex items-center gap-2 bg-black/50 text-white px-3 py-1 rounded-full">
            <Clock className="w-4 h-4" />
            <span className="font-mono">{timeRemaining}s</span>
          </div>
        )}
      </div>

      {/* Current challenge instruction */}
      {isRunning && currentChallenge && (
        <div className="mt-6 flex flex-col items-center">
          <div className="flex items-center gap-3 text-blue-600 mb-2">
            {CHALLENGE_ICONS[currentChallenge]}
            <span className="text-lg font-medium">
              {CHALLENGE_INSTRUCTIONS[currentChallenge]}
            </span>
          </div>
          {currentChallenge === 'BLINK' && (
            <p className="text-sm text-gray-500">
              Piscadas detectadas: {blinkCount}/2
            </p>
          )}
        </div>
      )}

      {/* Start button */}
      {!isRunning && results.length === 0 && (
        <button
          onClick={startChallenge}
          className="mt-6 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Iniciar Verificação de Vivacidade
        </button>
      )}
    </div>
  );
}
